import React, { useState, useEffect, useMemo, useRef } from "react";
import AdminLayout from "../../Layout/index.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { Table } from "flowbite-react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { gql, useLazyQuery } from "@apollo/client";
import { FaDownload } from "react-icons/fa6";
import Loader from "../../../../components/Loader.jsx";

// Leaderboard Query
const GET_MODULE_LEADERBOARD = gql`
  query getModuleLeaderboard(
    $moduleId: String!
    $campusName: String!
    $dayIds: [String!]
  ) {
    getModuleLeaderboard(
      moduleId: $moduleId
      campusName: $campusName
      dayIds: $dayIds
    ) {
      id
      student_id
      score
      is_completed
      question_id
    }
  }
`;

const getPositionIcon = (position) => {
  if (position === 1) return <span className="text-yellow-500">🥇</span>;
  if (position === 2) return <span className="text-gray-400">🥈</span>;
  if (position === 3) return <span className="text-yellow-800">🥉</span>;
  return position;
};

// CSV download helper
const downloadCSV = (data, studentInfo, moduleDetails) => {
  const headers = ["Rank", "Student Name"];

  // Dynamic headers with enabled_dates
  moduleDetails?.daywise_plan?.forEach((day, idx) => {
    const date = moduleDetails?.enabled_dates?.[idx];
    const formattedDate = date
      ? new Date(date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";
    headers.push(
      `${day.name || `Day ${idx + 1}`} ${
        formattedDate ? `(${formattedDate})` : ""
      } - (${day.questions?.length || 0} Questions)`
    );
  });

  headers.push("Total Score");

  const rows = data.map((entry, index) => {
    const row = [
      index + 1,
      studentInfo[entry.student_id]?.name || entry.student_id,
    ];

    moduleDetails?.daywise_plan?.forEach((day) => {
      const completed = day.questions.filter(
        (qid) => entry.questions[qid]?.is_completed
      ).length;
      row.push(completed);
    });

    row.push(entry.totalScore);
    return row;
  });

  const csvContent =
    "data:text/csv;charset=utf-8," +
    [headers, ...rows].map((e) => e.join(",")).join("\n");

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "module_leaderboard.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ModuleReport = () => {
  const { campusName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const moduleDetails = location.state?.name;
  const moduleId = moduleDetails?._id;
  const moduleName = moduleDetails?.name;

  const preselectedIndexes = location.state?.selectedDayIndexes;

  const [studentInfo, setStudentInfo] = useState({});
  const [clusterNameMap, setClusterNameMap] = useState({});
  const [selectedDayIndexes, setSelectedDayIndexes] = useState(preselectedIndexes || []);
  const [appliedDayIndexes, setAppliedDayIndexes] = useState(preselectedIndexes || []);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(!!preselectedIndexes);
  const [isDayFilterOpen, setIsDayFilterOpen] = useState(!preselectedIndexes);
  const [isInitialFilterPrompt, setIsInitialFilterPrompt] = useState(!preselectedIndexes);
  const hasInitDayFiltersRef = useRef(false);
  const [getStudent] = useLazyQuery(gql`
    query GetStudent($id: String!) {
      getStudent(id: $id) {
        id
        name
        department
        clusterIds
      }
    }
  `);
  const [getClustersByIds] = useLazyQuery(gql`
    query GetClustersByIdsAndCampus($clusterIds: [String!]!, $campusName: String!) {
      getClustersByIdsAndCampus(clusterIds: $clusterIds, campusName: $campusName) {
        id
        name
      }
    }
  `);

  const buildDayIds = (indexes) => {
    const days = moduleDetails?.daywise_plan || [];
    if (days.length === 0) return undefined;

    const safeIndexes = indexes.length > 0
      ? indexes
      : days.map((_, idx) => idx);

    const ids = safeIndexes
      .map((idx) => days[idx]?.day_id)
      .filter(Boolean);

    return ids.length > 0 ? ids : undefined;
  };

  const selectedDayIds = useMemo(
    () => buildDayIds(appliedDayIndexes),
    [moduleDetails, appliedDayIndexes]
  );

  const dayFilterLabel = useMemo(() => {
    const total = moduleDetails?.daywise_plan?.length || 0;
    if (total === 0) return "Select date";
    if (selectedDayIndexes.length === 0) {
      return "Select date";
    }
    if (selectedDayIndexes.length === total) {
      return "Select all";
    }
    if (selectedDayIndexes.length === 1) {
      const idx = selectedDayIndexes[0];
      const date = moduleDetails?.enabled_dates?.[idx];
      return date
        ? new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
        : `Day ${idx + 1}`;
    }
    return `${selectedDayIndexes.length} selected`;
  }, [moduleDetails, selectedDayIndexes]);

  useEffect(() => {
    if (hasInitDayFiltersRef.current) return;
    const days = moduleDetails?.daywise_plan || [];
    if (days.length === 0) return;

    setSelectedDayIndexes([]);
    hasInitDayFiltersRef.current = true;
  }, [moduleDetails]);

  const selectedQuestionIds = useMemo(() => {
    const days = moduleDetails?.daywise_plan || [];
    if (days.length === 0) return new Set();

    const indexes = appliedDayIndexes.length > 0
      ? appliedDayIndexes
      : days.map((_, idx) => idx);

    const ids = new Set();
    indexes.forEach((idx) => {
      const day = days[idx];
      if (!day?.questions) return;
      day.questions.forEach((qid) => ids.add(qid));
    });

    return ids;
  }, [moduleDetails, appliedDayIndexes]);

  const [fetchLeaderboard, { data, loading, error }] = useLazyQuery(
    GET_MODULE_LEADERBOARD
  );

  useEffect(() => {
    if (preselectedIndexes && moduleId && campusName) {
      const dayIds = buildDayIds(preselectedIndexes);
      fetchLeaderboard({
        variables: { moduleId, campusName, dayIds },
      });
    }
  }, []);

  // Fetch unique student info
  useEffect(() => {
    if (!data) return;

    const leaderboard = data.getModuleLeaderboard || [];
    const studentIds = [...new Set(leaderboard.map((entry) => entry.student_id))];

    studentIds.forEach(async (id) => {
      if (!studentInfo[id]) {
        const { data: studentData } = await getStudent({ variables: { id } });
        const student = studentData?.getStudent;
        if (student) {
          setStudentInfo((prev) => ({
            ...prev,
            [id]: {
              name: student.name,
              department: student.department || "",
              clusterIds: student.clusterIds || [],
            },
          }));
        }
      }
    });
  }, [data, getStudent, studentInfo]);

  useEffect(() => {
    const allClusterIds = [...new Set(
      Object.values(studentInfo).flatMap((student) => student.clusterIds || [])
    )];
    const missingClusterIds = allClusterIds.filter((id) => !clusterNameMap[id]);

    if (!campusName || missingClusterIds.length === 0) return;

    getClustersByIds({
      variables: { clusterIds: missingClusterIds, campusName },
    }).then(({ data: clustersData }) => {
      const clusters = clustersData?.getClustersByIdsAndCampus || [];
      if (clusters.length === 0) return;

      setClusterNameMap((prev) => {
        const next = { ...prev };
        clusters.forEach((cluster) => {
          next[cluster.id] = cluster.name;
        });
        return next;
      });
    });
  }, [studentInfo, campusName, getClustersByIds, clusterNameMap]);

  const leaderboard = data?.getModuleLeaderboard || [];
  const studentIds = [...new Set(leaderboard.map((entry) => entry.student_id))];
  const missingStudentIds = studentIds.filter((id) => !studentInfo[id]);
  const allClusterIds = [
    ...new Set(
      Object.values(studentInfo).flatMap((student) => student.clusterIds || [])
    ),
  ];
  const missingClusterIds = allClusterIds.filter((id) => !clusterNameMap[id]);
  const isDataLoading =
    loading ||
    (data && missingStudentIds.length > 0) ||
    (data && campusName && missingClusterIds.length > 0);

  if (isDataLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      </AdminLayout>
    );
  }
  if (error) return <div>Error loading leaderboard: {error.message}</div>;

  // Process leaderboard
  const processedLeaderboard = Object.values(
    leaderboard.reduce((acc, entry) => {
      if (!acc[entry.student_id]) {
        acc[entry.student_id] = {
          student_id: entry.student_id,
          questions: {},
        };
      }

      const studentData = acc[entry.student_id];
      const qid = entry.question_id;

      if (
        !studentData.questions[qid] ||
        entry.score > studentData.questions[qid].score
      ) {
        studentData.questions[qid] = {
          score: entry.score,
          is_completed: entry.is_completed,
        };
      }

      return acc;
    }, {})
  ).map((student) => {
    const questionIds = selectedQuestionIds.size > 0
      ? Array.from(selectedQuestionIds)
      : Object.keys(student.questions);

    const totalScore = questionIds.reduce(
      (sum, qid) => sum + (student.questions[qid]?.score || 0),
      0
    );
    const completedCount = questionIds.reduce(
      (sum, qid) => sum + (student.questions[qid]?.is_completed ? 1 : 0),
      0
    );

    return {
      ...student,
      totalScore,
      completedCount,
    };
  });

  processedLeaderboard.sort((a, b) => b.totalScore - a.totalScore);

  const displayedLeaderboard = appliedDayIndexes.length > 0
    ? processedLeaderboard.filter(
        (entry) => entry.totalScore > 0 || entry.completedCount > 0
      )
    : processedLeaderboard;

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-8 pt-5 m-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            Module Report - {moduleName || moduleId}
          </h2>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() =>
                navigate(`/admin/reports/module/${campusName}/${moduleId}/not-attended`, {
                  state: { moduleDetails },
                })
              }
              className="h-10 px-4 text-sm border border-[#5c7a94] rounded-lg bg-[#5c7a94] text-white mt-4"
            >
              Not Attended
            </button>
            <div className="relative flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">
                Date filter
              </span>
              <button
                type="button"
                onClick={() => setIsDayFilterOpen((prev) => !prev)}
                className="h-10 px-3 text-sm border border-gray-200 rounded-lg bg-white flex items-center justify-between gap-3 min-w-[160px]"
              >
                <span className="text-gray-800">{dayFilterLabel}</span>
                <span className="text-gray-400">v</span>
              </button>
              {isDayFilterOpen && isInitialFilterPrompt && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                  <div className="w-full max-w-sm bg-white border border-gray-200 rounded-xl shadow-xl p-4">
                    <div className="mb-2 text-sm font-semibold text-gray-800">
                      Select date filter
                    </div>
                    <div className="text-xs text-gray-500 mb-3">
                      Choose dates to load the leaderboard.
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={
                            moduleDetails?.daywise_plan?.length > 0 &&
                            selectedDayIndexes.length === moduleDetails.daywise_plan.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDayIndexes(
                                moduleDetails?.daywise_plan?.map((_, idx) => idx) || []
                              );
                            } else {
                              setSelectedDayIndexes([]);
                            }
                          }}
                          className="accent-blue-600"
                        />
                        All
                      </label>
                    </div>
                    <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                      {moduleDetails?.daywise_plan?.map((day, idx) => {
                        const date = moduleDetails?.enabled_dates?.[idx];
                        const formattedDate = date
                          ? new Date(date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : `Day ${idx + 1}`;
                        return (
                          <label key={day.day_id || idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={selectedDayIndexes.includes(idx)}
                              onChange={() => {
                                setSelectedDayIndexes((prev) =>
                                  prev.includes(idx)
                                    ? prev.filter((i) => i !== idx)
                                    : [...prev, idx]
                                );
                              }}
                              className="accent-blue-600"
                            />
                            {formattedDate}
                          </label>
                        );
                      })}
                    </div>
                    <div className="flex justify-end gap-2 pt-3">
                      <button
                        type="button"
                        onClick={() => setSelectedDayIndexes([])}
                        className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const nextDayIds = buildDayIds(selectedDayIndexes);
                          setAppliedDayIndexes(selectedDayIndexes);
                          setHasAppliedFilter(true);
                          if (moduleId && campusName) {
                            fetchLeaderboard({
                              variables: {
                                moduleId,
                                campusName,
                                dayIds: nextDayIds,
                              },
                            });
                            console.log("getModuleLeaderboard variables", {
                              moduleId,
                              campusName,
                              dayIds: nextDayIds,
                            });
                          }
                          setIsDayFilterOpen(false);
                          setIsInitialFilterPrompt(false);
                        }}
                        className="px-3 py-1.5 text-sm text-white bg-gray-500 border border-dashed border-gray-400 rounded"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {isDayFilterOpen && !isInitialFilterPrompt && (
                <div className="absolute right-0 top-full mt-1 w-[280px] bg-white border border-gray-200 rounded-lg shadow-md p-3 z-20">
                  <div className="absolute -top-2 right-6 h-3 w-3 rotate-45 bg-white border border-gray-200 border-b-0 border-r-0" />
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={
                          moduleDetails?.daywise_plan?.length > 0 &&
                          selectedDayIndexes.length === moduleDetails.daywise_plan.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDayIndexes(
                              moduleDetails?.daywise_plan?.map((_, idx) => idx) || []
                            );
                          } else {
                            setSelectedDayIndexes([]);
                          }
                        }}
                        className="accent-blue-600"
                      />
                      All
                    </label>
                  </div>
                  <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
                    {moduleDetails?.daywise_plan?.map((day, idx) => {
                      const date = moduleDetails?.enabled_dates?.[idx];
                      const formattedDate = date
                        ? new Date(date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : `Day ${idx + 1}`;
                      return (
                        <label key={day.day_id || idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedDayIndexes.includes(idx)}
                            onChange={() => {
                              setSelectedDayIndexes((prev) =>
                                prev.includes(idx)
                                  ? prev.filter((i) => i !== idx)
                                  : [...prev, idx]
                              );
                            }}
                            className="accent-blue-600"
                          />
                          {formattedDate}
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        const nextDayIds = buildDayIds(selectedDayIndexes);
                        setAppliedDayIndexes(selectedDayIndexes);
                        setHasAppliedFilter(true);
                        if (moduleId && campusName) {
                          fetchLeaderboard({
                            variables: {
                              moduleId,
                              campusName,
                              dayIds: nextDayIds,
                            },
                          });
                          console.log("getModuleLeaderboard variables", {
                            moduleId,
                            campusName,
                            dayIds: nextDayIds,
                          });
                        }
                        setIsDayFilterOpen(false);
                      }}
                      className="px-3 py-1.5 text-sm text-white bg-gray-500 border border-dashed border-gray-400 rounded"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() =>
                downloadCSV(displayedLeaderboard, studentInfo, moduleDetails)
              }
              className="inline-flex items-center justify-center w-10 h-10 text-gray-600 transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-900 mt-4"
              title="Download"
              aria-label="Download"
            >
              <FaDownload size={18} />
            </button>
          </div>
        </div>
        {!hasAppliedFilter && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Select date filters and click Apply to load the leaderboard.
          </div>
        )}
        {hasAppliedFilter && (
          <>
            {/* Table with horizontal scroll */}
            <div className="overflow-x-auto">
              <Table className="w-full min-w-[1200px] border-separate border-spacing-y-3">
            <Table.Head>
              <Table.HeadCell
                style={{ backgroundColor: "#5c7a94" }}
                className="rounded-l-lg text-xs font-semibold uppercase tracking-wide text-white text-center whitespace-nowrap"
              >
                Position
              </Table.HeadCell>
              <Table.HeadCell
                style={{ backgroundColor: "#5c7a94" }}
                className="text-xs font-semibold uppercase tracking-wide text-white text-center whitespace-nowrap"
              >
                Name
              </Table.HeadCell>
              <Table.HeadCell
                style={{ backgroundColor: "#5c7a94" }}
                className="text-xs font-semibold uppercase tracking-wide text-white text-center whitespace-nowrap"
              >
                Department
              </Table.HeadCell>
              <Table.HeadCell
                style={{ backgroundColor: "#5c7a94" }}
                className="text-xs font-semibold uppercase tracking-wide text-white text-center whitespace-nowrap min-w-[180px]"
              >
                Cluster
              </Table.HeadCell>

              {/* Dynamic headers with enabled_dates */}
              {moduleDetails?.daywise_plan?.map((day, idx) => {
                if (
                  appliedDayIndexes.length > 0 &&
                  !appliedDayIndexes.includes(idx)
                ) {
                  return null;
                }

                const date = moduleDetails?.enabled_dates?.[idx];
                const formattedDate = date
                  ? new Date(date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })
                  : null;

                return (
                  <Table.HeadCell
                    key={day.day_id || idx}
                    style={{ backgroundColor: "#5c7a94" }}
                    className="text-center text-xs font-semibold uppercase tracking-wide text-white whitespace-nowrap"
                  >
                    <div>{formattedDate || `Day ${idx + 1}`}</div>
                    <div className="">
                      {day.questions?.length || 0} Questions
                    </div>
                  </Table.HeadCell>
                );
              })}

              <Table.HeadCell
                style={{ backgroundColor: "#5c7a94" }}
                className="rounded-r-lg text-center text-xs font-semibold uppercase tracking-wide text-white whitespace-nowrap"
              >
                Total Score
              </Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {displayedLeaderboard.map((entry, index) => (
                <Table.Row
                  key={entry.student_id}
                  className="bg-white shadow-sm"
                >
                  {(() => {
                    const student = studentInfo[entry.student_id];
                    const clusterNames = (student?.clusterIds || [])
                      .map((id) => clusterNameMap[id] || id)
                      .filter(Boolean)
                      .join(", ");

                    return (
                      <>
                        <Table.Cell className="rounded-l-lg text-center font-semibold text-slate-700">
                          {getPositionIcon(index + 1)}
                        </Table.Cell>
                        <Table.Cell className="text-center font-semibold text-slate-800">
                          {student?.name || entry.student_id}
                        </Table.Cell>
                        <Table.Cell className="text-center text-slate-600">
                          {student?.department || "—"}
                        </Table.Cell>
                        <Table.Cell className="text-center text-slate-600 min-w-[180px]">
                          {clusterNames || "—"}
                        </Table.Cell>
                      </>
                    );
                  })()}

                  {/* Completed per day */}
                  {moduleDetails?.daywise_plan?.map((day, idx) => {
                    if (
                      appliedDayIndexes.length > 0 &&
                      !appliedDayIndexes.includes(idx)
                    ) {
                      return null;
                    }

                    const completed = day.questions.filter(
                      (qid) => entry.questions[qid]?.is_completed
                    ).length;
                    return (
                      <Table.Cell key={day.day_id || idx} className="text-center text-slate-700">
                        {completed}
                      </Table.Cell>
                    );
                  })}

                  <Table.Cell className="rounded-r-lg text-center font-semibold text-slate-800">
                    {entry.totalScore}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
              </Table>
            </div>
            {displayedLeaderboard.length === 0 && (
              <div className="mt-6 text-center text-sm text-gray-500">
                No students attended for the selected date(s).
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ModuleReport;
